// Services for requirements
import { BASE_URL } from '../constants';
import { getAuthHeader } from '../utils';

/**
 * @typedef APIRequirementTypes
 * @property {number} id
 * @property {string} name
 * @property {boolean} has_space `true` if can select daily quota for this type
 */

/**
 * @typedef APIDiagnosis
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef APIRequirement
 * @property {number} id
 * @property {string} name
 * @property {number} type_requirement
 * @property {string} type_requirement_name
 */

/**
 * Get list of requirements
 * @param {number | undefined} typeId Requirement type ID
 * @param {number[]} companies List of company IDs
 */
export async function getRequirements(
  typeId,
  companies = [],
  state = null,
  medicalCenter = null,
  isActive = true
) {
  try {
    const params = new URLSearchParams();
    if (companies.length > 0) {
      companies.forEach(company => {
        if (company !== null) {
          params.append('companies', company);
        }
      });
    }
    if (state) {
      params.append('state', state);
    }
    if (typeId) {
      params.append('type_requirement', typeId);
    }
    if (medicalCenter) {
      params.append('medical_center', medicalCenter);
    }
    if (isActive !== null) {
      params.append('is_active', isActive ? 'true' : 'false');
    }
    // agregar query argumentos al fetch
    const response = await fetch(BASE_URL + 'insurance/requirement/?' + params.toString(), {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    /**@type {APIRequirement[]} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching requirements: ', error.toString());
    return [];
  }
}

/**
 * Get medical centers that can attend a requirement
 * @deprecated
 * @param {number | undefined} requirementId
 */
export async function getRequirementMedicalCenters(requirementId) {
  try {
    let query = requirementId;
    if (!requirementId) {
      query = '';
    }
    const response = await fetch(BASE_URL + 'requirement-medical-center/?requirement=' + query, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    const data = await response.json();
    return data.results;
  } catch (error) {
    console.error('Error fetching medical centers for requirement: ', error);
    return [];
  }
}

/**
 * Get list of requirement types
 */
export async function getRequirementTypes() {
  try {
    const response = await fetch(BASE_URL + 'insurance/type-requiremet/', {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    const data = await response.json();
    /**@type {APIRequirementTypes[]} */
    const results = data.results;
    return results;
  } catch (error) {
    console.error('Error fetching requirement types: ', error);
    return [];
  }
}

/**
 * Get list of diagnosis
 */
export async function getDiagnosisList() {
  try {
    const timestamp = new Date().getTime();
    const response = await fetch(`${BASE_URL}insurance/medical-diagnosis/?_=${timestamp}`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    const data = await response.json();

    /**@type {APIDiagnosis[]} */
    let results = data.results;

    // Get all data from pagination
    let nextUrl = data.next;
    while (nextUrl) {
      const nextResponse = await fetch(nextUrl, {
        headers: {
          ...getAuthHeader(),
        },
      });
      if (!nextResponse.ok) {
        break;
      }
      const nextData = await nextResponse.json();
      results = results.concat(nextData.results);
      nextUrl = nextData.next;
    }

    return results;
  } catch (error) {
    console.error('Error fetching diagnosis list: ', error);
    return [];
  }
}

/**
 * Add a new requirement
 * @param {object} requirement
 * @param {number} requirement.requirementType Requirement type ID
 * @param {string} requirement.name Requirement name
 */
export async function addRequirement({ requirementType, name, isActive = undefined }) {
  try {
    const payload = {
      type_requirement: requirementType,
      name: name,
    };
    if (isActive !== undefined) {
      payload.is_active = isActive;
    }
    const response = await fetch(BASE_URL + 'insurance/requirement/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (response.status !== 201) {
      throw new Error(JSON.stringify(response));
    }
    const data = await response.json();
    return { type: 'success', message: 'Requerimiento agregado correctamente', data };
  } catch (error) {
    console.error('Error adding requirement: ', error);
    return { type: 'error', message: 'Error al agregar el requerimiento' };
  }
}

/**
 * Update requirement data
 * @param {object} params
 * @param {number} params.id Requirement ID
 * @param {number} params.type Requirement type ID
 * @param {string} params.name Requirement name
 */
export async function editRequirement({ id, type, name, is_active }) {
  try {
    const response = await fetch(BASE_URL + 'insurance/requirement/' + id + '/', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify({
        type_requirement: type,
        name,
        is_active,
      }),
    });
    if (response.status !== 200) {
      throw new Error(JSON.stringify(response));
    }
    return { type: 'success', message: 'Requerimiento editado correctamente' };
  } catch (error) {
    console.error('Error editing requirement: ', error);
    return { type: 'error', message: 'Error al editar el requerimiento' };
  }
}

// Servicio para crear un nuevo requerimiento y asociarlo a uno o varios centros médicos
export async function createAndAssociateRequirement({
  name,
  type_requirement,
  medical_center_id, // opcional (para 1 centro)
  medical_center_ids, // opcional (para varios centros)
  client_id = null,
  subAdministratorId = null,
  price = 0,
}) {
  try {
    // Validación: debe haber al menos uno de los dos
    if (
      medical_center_id == null &&
      (!medical_center_ids || !Array.isArray(medical_center_ids) || medical_center_ids.length === 0)
    ) {
      throw new Error("Debe proporcionar 'medical_center_id' o 'medical_center_ids'");
    }

    const payload = {
      name: name.trim(),
      type_requirement,
      price,
    };
    if (client_id) {
      payload.client_id = client_id;
    }
    if (subAdministratorId) {
      payload.sub_administrator = subAdministratorId;
    }

    // Enviar el campo apropiado
    if (medical_center_ids && medical_center_ids.length > 0) {
      payload.medical_center_ids = medical_center_ids;
    } else {
      payload.medical_center_id = medical_center_id;
    }

    const response = await fetch(BASE_URL + 'insurance/requirement-collective/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });

    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData.detail || `Error ${response.status}`);
    }

    return {
      type: 'success',
      message: responseData.message,
      data: responseData.data,
    };
  } catch (error) {
    console.error('Error en createAndAssociateRequirement:', error);
    return {
      type: 'error',
      message: error.message,
    };
  }
}

export async function getRequirementAliasList({
  page = 1,
  pageSize = 20,
  requirementName = null,
  aliasName = null,
  clientId = null,
}) {
  const params = new URLSearchParams();
  params.append('page', page);
  params.append('page_size', pageSize);
  if (requirementName) {
    params.append('requirement__name__icontains', requirementName);
  }
  if (aliasName) {
    params.append('alias_requirement__name__icontains', aliasName);
  }
  if (clientId) {
    params.append('company_client', clientId);
  }

  const response = await fetch(`${BASE_URL}insurance/requirement-alias/?${params.toString()}`, {
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    throw new Error(`Error ${response.status}`);
  }

  const data = await response.json();

  return data;
}

export async function createRequirementAlias({ requirementId, aliasId, clientId }) {
  const payload = {
    requirement: requirementId,
    alias_requirement: aliasId,
    company_client: clientId,
  };

  const response = await fetch(`${BASE_URL}insurance/requirement-alias/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Error ${response.status}`);
  }

  const data = await response.json();

  return data;
}

export async function deleteRequirementAlias(aliasId) {
  const response = await fetch(`${BASE_URL}insurance/requirement-alias/${aliasId}/`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeader(),
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.detail || `Error ${response.status}`);
  }

  return { type: 'success', message: 'Alias eliminado correctamente' };
}
